import React, { useState } from 'react'
import { Box, Divider, Typography } from '@mui/material'
import Delete from '@assets/Analytics/DeleteGreen.svg'
import { useAppTheme } from '@/theme/ThemeContext'
import ConfirmDeleteDialog from '@/components/Analytics/ConfirmDeleteDialog'
import DeleteActionButton from '@/components/Analytics/DeleteActionButton'

type Props = {
  width?: number | string
  height?: number
  title?: string
  treeName?: string
  selectedRowId?: Array<string | number>
  onRemove?: () => void | Promise<void>
}

const DeleteConsumerPanel: React.FC<Props> = ({
  width = 460,
  height = 'auto',
  title = 'הסרת צרכנים מעץ המים',
  treeName,
  selectedRowId,
  onRemove,
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const count = selectedRowId?.length ?? 0
  const hasSelection = count > 0
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [removing, setRemoving] = useState(false)

  const handleConfirm = async () => {
    setRemoving(true)
    try {
      await onRemove?.()
    } finally {
      setRemoving(false)
      setConfirmOpen(false)
    }
  }

  const bgColor = isDarkMode ? '#1D263B' : '#FAFBFD'
  const textColor = isDarkMode ? '#FFFFFF' : '#003F61'
  const borderColor = isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'

  return (
    <Box
      sx={{
        border: `1px solid ${borderColor}`,
        borderRadius: '14px',
        backgroundColor: bgColor,
        width: width,
        height: height,
        minHeight: 180,
        px: 3.5,
        py: 2.5,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'none',
        direction: 'rtl'
      }}
    >
      {/* Header - Aligned Right */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1.2, mb: 1.5 }}>
        <Box component="img" src={Delete} sx={{ width: 18, height: 18 }} />
        <Typography sx={{ fontSize: '18px', fontWeight: 200, color: textColor, fontFamily: 'Rubik' }}>
          {title}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: borderColor, mb: 0 }} />
      
      {/* Center content - Description text (Vertically and Horizontally centered in body) */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        <Typography sx={{ 
            fontSize: '15px', 
            fontWeight: 200, 
            lineHeight: 1.5,
            color: isDarkMode ? '#94A3B8' : '#64748B', 
            fontFamily: 'Rubik' 
        }}>
          {count > 0 
            ? `נבחרו ${count} שורות למחיקה בטבלת הצרכנים של עץ המים.`
            : 'בחר צרכנים מהטבלה כדי להסיר אותם מעץ המים.'}
        </Typography>
      </Box>

      {/* Footer action - Button toward the left side */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
        <DeleteActionButton
          disabled={!hasSelection || removing}
          onClick={() => setConfirmOpen(true)}
        >
          מחיקת צרכנים
        </DeleteActionButton>
      </Box>

      <ConfirmDeleteDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        busy={removing}
        title={`למחוק ${count} צרכנים מעץ המים?`}
        content={`${count} הצרכנים שנבחרו יוסרו מעץ המים${treeName ? ` "\u2068${treeName}\u2069"` : ''}. לא יהיה ניתן לשחזר את הפעולה.`}
      />
    </Box>
  )
}

export default DeleteConsumerPanel
